import { useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Link from '@mui/material/Link';
import { Button } from '@startengine/design-system.ui.button';
import { Modal } from '@startengine/design-system.ui.modal';
import useIsMobile from 'hooks/useIsMobile';
import { imagePath } from 'utils/helpers';
import { trackEvent } from 'utils/analytics';
import usePopupTrigger, { safeWriteStorage } from './usePopupTrigger';

const CAMPAIGN_URL = 'https://www.startengine.com/offering/startengine';
const ANNUAL_REPORTS_URL = 'https://www.startengine.com/annual-reports';

const POPUP_EVENTS = {
  shown: 'private_popup_shown',
  dismissed: 'private_popup_dismissed',
  ctaClicked: 'private_popup_cta_clicked',
};

const INK = '#02100E';
const GOLD_TEXT = '#E4C885';
const GOLD_GRADIENT =
  'linear-gradient(97deg, #AB7B2E 0%, #BC9245 30%, #CFAC66 55%, #E3C886 80%, #F0D999 100%)';

const LOGOS = [
  { src: 'anthropic-dark.png', alt: 'Anthropic', height: 24, mobileHeight: 16 },
  { src: 'discord-dark.png', alt: 'Discord', height: 26, mobileHeight: 18 },
  { src: 'perplexity-dark.png', alt: 'Perplexity', height: 26, mobileHeight: 18 },
  { src: 'xai-dark.png', alt: 'xAI', height: 30, mobileHeight: 20 },
  { src: 'stripe-dark.png', alt: 'Stripe', height: 28, mobileHeight: 19 },
];

const ClosedCampaignPopup = ({
  variant = 'closed-campaign',
  utmContent = 'closed-offering-popup',
  storageKey = 'closedOfferingCampaignDismissed',
  scrollPercent = 30,
  delayMs = 7000,
  enabled = true,
}) => {
  const isMobile = useIsMobile();
  const targetUrl = `${CAMPAIGN_URL}?utm_content=${encodeURIComponent(utmContent)}`;

  const handleOpen = useCallback(
    (trigger) => {
      trackEvent(POPUP_EVENTS.shown, { variant, utmContent, trigger });
    },
    [variant, utmContent],
  );

  const { open, dismiss } = usePopupTrigger({
    enabled,
    storageKey,
    scrollPercent,
    delayMs,
    onOpen: handleOpen,
  });

  const handleClose = () => {
    dismiss();
    trackEvent(POPUP_EVENTS.dismissed, { variant, utmContent });
  };

  const handleCtaClick = () => {
    trackEvent(POPUP_EVENTS.ctaClicked, { variant, utmContent });
    dismiss();
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  };

  const handleReportsClick = () => {
    safeWriteStorage(storageKey);
  };

  if (!enabled) return null;

  const fineprintSx = {
    display: 'block',
    color: INK,
    fontSize: isMobile ? '11px' : '12px',
    lineHeight: 1.4,
    letterSpacing: 0,
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      bottom={isMobile}
      hideHeader
      backgroundColor="transparent"
      data-cy="closed-campaign-popup"
      data-cy-variant={variant}
      data-block-recording
      sx={{
        '.modal-container': {
          width: isMobile ? '100%' : '852px',
          maxWidth: isMobile ? '100%' : 'calc(100vw - 48px) !important',
          borderRadius: isMobile ? '32px 32px 0 0' : '40px !important',
          overflow: 'hidden',
          boxShadow: '0 25px 50px rgba(0,0,0,0.3)',
          backgroundColor: 'transparent !important',
          background: 'transparent !important',
          padding: '0 !important',
          margin: '0 !important',
        },
        '.modal-body': {
          overflow: 'hidden',
          padding: '0 !important',
          margin: '0 !important',
          backgroundColor: 'transparent !important',
          background: 'transparent !important',
        },
        '.modal-footer': {
          display: 'none !important',
        },
        '.MuiGrid-container': {
          padding: '0 !important',
          margin: '0 !important',
          background: 'transparent !important',
        },
        '.MuiGrid-item': {
          padding: '0 !important',
        },
      }}
    >
      <Box
        sx={{
          background: GOLD_GRADIENT,
          borderRadius: isMobile ? '32px 32px 0 0' : '40px',
          padding: isMobile ? '32px 20px 24px' : '56px 62px 48px',
          position: 'relative',
          color: INK,
        }}
      >
        <IconButton
          onClick={handleClose}
          aria-label="Close popup"
          data-cy="closed-campaign-popup-close"
          sx={{
            position: 'absolute',
            top: isMobile ? '12px' : '24px',
            right: isMobile ? '12px' : '24px',
            color: INK,
            '&:hover': {
              backgroundColor: 'rgba(2, 16, 14, 0.08)',
            },
          }}
        >
          <CloseIcon fontSize={isMobile ? 'small' : 'medium'} />
        </IconButton>

        <Typography
          component="h2"
          sx={{
            color: INK,
            fontFamily: '"owners-text", Georgia, serif',
            fontWeight: 400,
            fontSize: isMobile ? '28px' : '56px',
            lineHeight: 1.08,
            letterSpacing: '-0.01em',
            pr: isMobile ? 4 : 6,
          }}
        >
          This Offering is Closed, But StartEngine&rsquo;s Latest Campaign Just Launched
        </Typography>

        <Box
          sx={{
            borderTop: `1px solid ${INK}`,
            mt: isMobile ? 2.5 : 5,
          }}
        />

        <Typography
          sx={{
            color: INK,
            fontFamily: '"owners-text", Georgia, serif',
            fontSize: isMobile ? '15px' : '19px',
            lineHeight: 1.4,
            mt: isMobile ? 2 : 3.5,
          }}
        >
          We&rsquo;ve doubled revenue each of the last two years, reaching $109M in 2025. Now you
          can join 50K shareholders who&rsquo;ve invested $100M+ In one of the largest pre-IPO
          investing platforms in the U.S.
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: isMobile ? '16px 20px' : '20px',
            mt: isMobile ? 3 : 4,
          }}
        >
          {LOGOS.map((logo) => (
            <Box
              key={logo.alt}
              component="img"
              src={`${imagePath}/images/offering/${logo.src}`}
              alt={logo.alt}
              data-cy={`closed-campaign-popup-logo-${logo.alt.toLowerCase()}`}
              sx={{
                height: isMobile ? `${logo.mobileHeight}px` : `${logo.height}px`,
                width: 'auto',
                objectFit: 'contain',
              }}
            />
          ))}
        </Box>

        <Button
          variant="contained"
          onClick={handleCtaClick}
          data-cy="closed-campaign-popup-cta"
          sx={{
            backgroundColor: INK,
            color: `${GOLD_TEXT} !important`,
            fontWeight: 700,
            padding: isMobile ? '16px 28px' : '20px 40px',
            fontSize: isMobile ? '16px' : '17px',
            lineHeight: 1.2,
            borderRadius: '12px',
            textTransform: 'none',
            width: isMobile ? '100%' : 'auto',
            minWidth: isMobile ? 0 : '413px',
            mt: isMobile ? 3 : 4,
            '&:hover': {
              backgroundColor: '#0A241D',
            },
          }}
        >
          View Opportunity
        </Button>

        <Typography variant="caption" sx={{ ...fineprintSx, mt: isMobile ? 3 : 4 }}>
          This Reg A+ offering is made available through StartEngine, Inc. No broker-dealer or
          intermediary involved in offering. This investment is speculative, illiquid, and involves
          a high degree of risk, including the possible loss of your entire investment. For more
          information, please see the most recent Offering Circular and Risks related to this
          offering, or{' '}
          <Link
            href={ANNUAL_REPORTS_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleReportsClick}
            data-cy="closed-campaign-popup-annual-reports"
            sx={{ color: INK, textDecorationColor: INK, fontWeight: 'inherit' }}
          >
            StartEngine&rsquo;s Annual and Quarterly Reports
          </Link>{' '}
          to review our latest financials.
        </Typography>

        <Typography variant="caption" sx={{ ...fineprintSx, mt: 2 }}>
          Note: In Q1 2026, our revenue was $25M (compared to $30.3M in Q1 2025). Subsequent annual
          results may not reflect the same growth rate. Past performance may not be indicative of
          future performance.
        </Typography>

        <Typography variant="caption" sx={{ ...fineprintSx, mt: 2 }}>
          MAR-14877
        </Typography>
      </Box>
    </Modal>
  );
};

export default ClosedCampaignPopup;
